/**
 * Formatting for /credit-notes. Swiss numbers, and two figures that need a decision rather
 * than a format string.
 */

const AMOUNT_2DP = new Intl.NumberFormat('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const AMOUNT_0DP = new Intl.NumberFormat('de-CH', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const PCT_2DP = new Intl.NumberFormat('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** A credited amount. Shown negative because that is what a credit note does to the books. */
export function formatCredited(value: number | null | undefined): string {
    if (value === null || value === undefined) return '–';
    // The ERP stores the amount POSITIVE and the sign carries no meaning there — the loader
    // says so explicitly. The minus is put on at the very last moment, for reading, and only
    // when there is something to negate: "−0.00" is not a number anyone should have to parse.
    const n = Math.abs(value);
    return n === 0 ? '0.00' : `−${AMOUNT_2DP.format(n)}`;
}

export function formatAmount(value: number | null | undefined): string {
    if (value === null || value === undefined) return '–';
    return AMOUNT_2DP.format(value);
}

export function formatAmountRounded(value: number | null | undefined): string {
    if (value === null || value === undefined) return '–';
    return AMOUNT_0DP.format(value);
}

export function formatQuantity(value: number | null | undefined): string {
    if (value === null || value === undefined) return '–';
    return new Intl.NumberFormat('de-CH', { maximumFractionDigits: 3 }).format(value);
}

export function formatCount(value: number | null | undefined): string {
    if (value === null || value === undefined) return '–';
    return value.toLocaleString('de-CH');
}

/** Two decimals — the same two the rule engine rounds to before it compares. */
export function formatPercent(value: number | null | undefined): string {
    if (value === null || value === undefined) return '–';
    return `${PCT_2DP.format(value)}%`;
}

/**
 * The share of the customer's yearly turnover, capped for display.
 *
 * `salesSharePct` reaches 18'180'800 %. Two dit credit notes, `VNNC26-00049` and
 * `VUNC26-00005`, each credit 1'818.08 to customer 105126, whose entire 2026 invoiced turnover
 * is ONE line over 0.01 EUR. The finding is real — two identical credit notes against a
 * customer with no turnover, and they fire on DUPLICATE too — but printed in full the number
 * is not a measurement, it is a column-breaking artefact of a denominator that is almost zero.
 *
 * So the ratio is capped at 1'000 % HERE, in the display layer only. The engine's value is
 * untouched, the verdict is untouched, and the drawer shows the two amounts side by side
 * instead of the ratio, which is the only form in which this particular finding reads true.
 */
export const SALES_SHARE_DISPLAY_CAP = 1000;

export function formatSalesShare(value: number | null | undefined): string {
    if (value === null || value === undefined) return '–';
    if (value > SALES_SHARE_DISPLAY_CAP) return `> ${AMOUNT_0DP.format(SALES_SHARE_DISPLAY_CAP)}%`;
    return formatPercent(value);
}

/** True when the ratio was capped, so the caller can point at the amounts instead. */
export function isSalesShareCapped(value: number | null | undefined): boolean {
    return value !== null && value !== undefined && value > SALES_SHARE_DISPLAY_CAP;
}

/**
 * How stale the ERP replicas are, in words that do not cry wolf.
 *
 * The load runs on SUNDAY–THURSDAY nights; Friday and Saturday have no run at all. So three
 * days old on a Sunday is the normal, healthy state of this data, and a page that paints it
 * amber teaches its reader to ignore the indicator by Monday. Only an age that cannot be
 * explained by the schedule is worth a colour.
 */
export function freshnessTone(ageInDays: number | null | undefined): 'normal' | 'stale' {
    if (ageInDays === null || ageInDays === undefined) return 'normal';
    // Thu night loads, Fri and Sat do not, so the oldest legitimate age is reached on Sunday
    // before that night's run: loaded Thursday night, read Sunday daytime = 3 days.
    return ageInDays > 3 ? 'stale' : 'normal';
}

/** yyyy-mm-dd for a date `months` before the first of this month. Never via toISOString. */
export function monthsAgoIso(months: number): string {
    const now = new Date();
    const d = new Date(now.getFullYear(), now.getMonth() - months, 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

/** yyyy-mm-dd for tomorrow, local calendar. The list window's exclusive upper bound. */
export function tomorrowIso(): string {
    const now = new Date();
    const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
